import { useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from 'react-query'
import { useState } from 'react'
import { t } from 'ttag'
import styled from 'styled-components'

import { AssignmentSummary } from '@h2/features/planning/assignments/assignmentSummary/index.js'
import { api } from '@h2/api'
import { Loader } from '@h2/components/loading/loader'
import { Button } from '@h2/components/buttons/button'
import { Heading } from '@h2/components/typography/heading'
import { Text } from '@h2/components/typography/text'
import { PB97T } from '@h2/components/colors'

import { ApprovalActions } from './components/approvalActions'
import { RejectionActions } from './components/rejectionActions'
import { ReassignmentActions } from './components/reassignmentActions'

const ButtonBarContainer = styled.div`
  background-color: ${PB97T};
  padding: 15px;
  display: flex;
`
const CurrentState = styled.div`
  background-color: ${PB97T};
  padding: 5px;
  margin-bottom: 15px;
`
const FlexStart = styled.div`
  flex: 1;
  align-items: flex-start;
`
const FlexEnd = styled.div`
  display: flex;
  gap: 5px;
  align-items: flex-end;
`
const ButtonBar = ({ assignmentId, assignment }) => {
  const queryClient = useQueryClient()
  const [modalShown, setModalShown] = useState(null)
  const modalStates = {
    approval: 'APPROVAL',
    rejection: 'REJECTION',
    reassignment: 'REASSIGNMENT'
  }
  const clearModal = () => {
    setModalShown(null)
  }
  const openApprovalModal = () => {
    setModalShown(modalStates.approval)
  }
  const openRejectionModal = () => {
    setModalShown(modalStates.rejection)
  }
  const openReassignmentModal = () => {
    setModalShown(modalStates.reassignment)
  }
  const onSuccess = updatedAssignment => {
    queryClient.setQueryData(['assignment', `${updatedAssignment.id}`], updatedAssignment)
  }
  let leftContent = null
  let rightContent = null
  //disabling reassignment as it isn't necessary at the moment
  const showReassignment = false

  if (showReassignment && !assignment.dateApproved) {
    leftContent = (
      <>
        <Button display="monochrome" variant="outlined" onClick={openReassignmentModal}>
          {t`Reassign to another Navigator`}
        </Button>
        <ReassignmentActions
          onSuccess={onSuccess}
          assignmentId={assignmentId}
          isOpen={modalShown === modalStates.reassignment}
          toggleModal={clearModal}
        />
      </>
    )
  }

  if (assignment.dateSubmitted && !assignment.dateApproved) {
    rightContent = (
      <>
        <Button display="default" onClick={openApprovalModal}>{t`Approve`}</Button>
        <Button display="red" onClick={openRejectionModal}>{t`Reject`}</Button>
        <ApprovalActions
          onSuccess={onSuccess}
          assignmentId={assignmentId}
          isOpen={modalShown === modalStates.approval}
          toggleModal={clearModal}
        />
        <RejectionActions
          onSuccess={onSuccess}
          assignmentId={assignmentId}
          isOpen={modalShown === modalStates.rejection}
          toggleModal={clearModal}
        />
      </>
    )
  }

  return (
    (leftContent || rightContent) && (
      <ButtonBarContainer>
        <FlexStart>{leftContent}</FlexStart>
        <FlexEnd>{rightContent}</FlexEnd>
      </ButtonBarContainer>
    )
  )
}
const AssignmentDetail = () => {
  const { assignmentId } = useParams()
  const { data: assignment, isFetching } = useQuery(
    ['assignment', `${assignmentId}`],
    () => {
      return api.fetch(`/management/assignments/${assignmentId}`, {
        method: 'GET'
      })
    }
  )

  if (isFetching) {
    return <Loader />
  }

  const calculateLastAction = () => {
    if (assignment.dateApproved) {
      return (
        <Text>
          <Text weight="bold" as="span">{t`Approved:`}</Text>{' '}
          {new Date(assignment.dateApproved).toLocaleDateString()}
        </Text>
      )
    }

    if (assignment.dateSubmitted) {
      return (
        <Text>
          <Text weight="bold" as="span">{t`Submitted:`}</Text>{' '}
          {new Date(assignment.dateSubmitted).toLocaleDateString()}
        </Text>
      )
    }

    return (
      <Text>
        <Text weight="bold" as="span">{t`Assigned:`}</Text>{' '}
        {new Date(assignment.dateCreated).toLocaleDateString()}
      </Text>
    )
  }
  const calculateCurrentState = () => {
    return <Text>{calculateLastAction()}</Text>
  }

  return (
    <div>
      <Heading level="3">{assignment.name}</Heading>
      <Text size="large">
        <b>{t`Explorer: `}</b> {assignment.assignee.name}
      </Text>
      <CurrentState state={assignment.status}>{calculateCurrentState()}</CurrentState>
      <AssignmentSummary
        hideTitle={true}
        assignment={assignment}
        hidePrint={true}
        readOnlyPreview={true}
      />
      <ButtonBar assignmentId={assignmentId} assignment={assignment}></ButtonBar>
    </div>
  )
}

export { AssignmentDetail }
